import React, { useEffect, useMemo, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { apiService } from '../services/apiService';
import SetupGuard from '../components/SetupGuard';

const emptyForm = { assessmentId: '', studentId: '', marksObtained: '', remarks: '' };

const Grades = () => {
  const [assessments, setAssessments] = useState([]);
  const [students, setStudents] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [assessmentData, studentData] = await Promise.all([
        apiService.getAll('assessments'),
        apiService.getAll('students'),
      ]);

      const gradeResults = await Promise.all(
        (assessmentData || []).map(item =>
          apiService.get(`grades/assessment/${item._id}`).catch(() => [])
        )
      );

      setAssessments(assessmentData || []);
      setStudents(studentData || []);
      setGrades(gradeResults.flat());
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load grades.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredGrades = useMemo(
    () =>
      grades.filter(item =>
        [item.studentName, item.assessmentTitle, item.type]
          .join(' ')
          .toLowerCase()
          .includes(search.toLowerCase())
      ),
    [grades, search]
  );

  const handleChange = event => {
    const { name, value } = event.target;
    setForm(previous => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    try {
      setSaving(true);
      setError('');
      await apiService.create('grades', form);
      setIsModalOpen(false);
      setForm(emptyForm);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save grade.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this grade record?')) return;
    try {
      await apiService.delete('grades', id);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete grade.');
    }
  };

  const selectedAssessment = assessments.find(item => String(item._id) === String(form.assessmentId));

  if (loading) return <LoadingSpinner message="Loading grades..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Grades</h1>
        <button className="btn btn-primary" onClick={() => setIsModalOpen(true)}>+ Record Grade</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <SetupGuard checks={[
        {
          ok: students.length > 0,
          icon: '🎓',
          label: 'No Students enrolled yet',
          description: 'Enrol students before recording grades.',
          action: { label: 'Enrol Student', to: '/students' },
        },
        {
          ok: assessments.length > 0,
          icon: '📝',
          label: 'No Assessments exist yet',
          description: 'Create assessments before recording grades.',
          action: { label: 'Create Assessment', to: '/exams' },
        },
      ]}>

      <div className="search-container">
        <input
          type="text"
          placeholder="Search grades..."
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
      </div>

      <div className="table-responsive">
        <table className="data-table">
          <thead>
            <tr><th>Student</th><th>Assessment</th><th>Marks</th><th>%</th><th>Remarks</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {filteredGrades.length === 0 ? (
              <tr><td colSpan={6} className="no-data-message">No grades found.</td></tr>
            ) : filteredGrades.map(item => (
              <tr key={item._id}>
                <td>{item.studentName}</td>
                <td>{item.assessmentTitle}</td>
                <td>{item.marksObtained} / {item.totalMarks}</td>
                <td>{item.percentage ?? 0}%</td>
                <td>{item.remarks || 'No remarks'}</td>
                <td>
                  <button className="btn-icon" onClick={() => handleDelete(item._id)} title="Delete">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      </SetupGuard>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Record Grade">
        <form onSubmit={handleSubmit} className="form">
          <div className="form-group">
            <label>Assessment</label>
            <select name="assessmentId" value={form.assessmentId} onChange={handleChange} required>
              <option value="">Select Assessment</option>
              {assessments.map(item => (
                <option key={item._id} value={item._id}>
                  {item.title} - {item.moduleName || item.subjectName} ({item.className})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Student</label>
            <select name="studentId" value={form.studentId} onChange={handleChange} required>
              <option value="">Select Learner</option>
              {students.map(student => (
                <option key={student._id} value={student._id}>
                  {student.firstName} {student.lastName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Marks Obtained</label>
              <input type="number" name="marksObtained" value={form.marksObtained} onChange={handleChange} min={0} max={selectedAssessment?.totalMarks || 100} required />
            </div>
            <div className="form-group">
              <label>Total Marks</label>
              <input type="text" value={selectedAssessment?.totalMarks || 'Select assessment'} readOnly />
            </div>
          </div>

          <div className="form-group">
            <label>Remarks</label>
            <textarea name="remarks" value={form.remarks} onChange={handleChange} rows={3} />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Grade'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Grades;
